import React, { useState, useEffect } from 'react';
import { convertToWebPDataUrl } from '../lib/imageUtils';
import { X, CreditCard, QrCode, ShieldCheck, Check, Key, Lock, Globe, DollarSign, ExternalLink, Settings, Save, AlertCircle, CheckCircle2, RefreshCw, Zap, Sparkles, Building, ChevronRight, Upload, Image, Trash2 } from 'lucide-react';
import { PaymentGateway } from '../types';
import { db } from '../lib/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { sanitizePaymentUrl } from '../lib/mobilePaymentUtils';

export interface PaymentGatewayConfig {
  id: PaymentGateway;
  name: string;
  badge: string;
  badgeColor: string;
  enabled: boolean;
  isDefault?: boolean;
  pixKey?: string;
  pixKeyType?: 'email' | 'cnpj' | 'phone' | 'random';
  pixQrCodeImage?: string;
  pixCopiaECola?: string;
  beneficiaryName?: string;
  bankName?: string;
  requireReceipt?: boolean;
  discountPercentage?: number;
  useTestMode?: boolean;
  testAccessToken?: string;
  testPublicKey?: string;
  testNotificationUrl?: string;
  productionAccessToken?: string;
  productionPublicKey?: string;
  productionNotificationUrl?: string;
  accessToken?: string;
  publicKey?: string;
  notificationUrl?: string;
  paymentLink?: string;
  paypalEmail?: string;
  paypalClientId?: string;
  stripePublishableKey?: string;
  stripeSecretKey?: string;
  infinitePayApiKey?: string;
  maxInstallments?: number;
  interestFreeInstallments?: number;
}

const DEFAULT_CONFIGS: Record<PaymentGateway, PaymentGatewayConfig> = {
  PIX: {
    id: 'PIX',
    name: 'PIX Instantâneo',
    badge: 'Sem Taxa Extra',
    badgeColor: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    enabled: true,
    isDefault: true,
    pixKey: 'financeiro@dojapao.co.jp',
    pixKeyType: 'email',
    beneficiaryName: 'Do Japão Logística & Intermediação LTDA',
    bankName: 'Banco Rendimento / Itaú Unibanco',
    requireReceipt: true,
    discountPercentage: 2
  },
  MercadoPago: {
    id: 'MercadoPago',
    name: 'Mercado Pago',
    badge: 'Até 12x no Cartão ou PIX',
    badgeColor: 'bg-sky-100 text-sky-800 border-sky-300',
    enabled: true,
    isDefault: false,
    useTestMode: true,
    testAccessToken: 'APP_USR-8392104920194821-073100-dojapao-test',
    testPublicKey: 'APP_USR-7e21a88b-1234-5678-90ab-cdef12345678-test',
    testNotificationUrl: 'https://ais-dev-3ye47l3lksx62k4motkf2a-59051396028.asia-east1.run.app/api/webhooks/mercadopago',
    productionAccessToken: 'APP_USR-8392104920194821-073100-dojapao-prod',
    productionPublicKey: 'APP_USR-7e21a88b-1234-5678-90ab-cdef12345678',
    productionNotificationUrl: 'https://dojapao.co.jp/api/webhooks/mercadopago',
    accessToken: 'APP_USR-8392104920194821-073100-dojapao-test',
    publicKey: 'APP_USR-7e21a88b-1234-5678-90ab-cdef12345678-test',
    notificationUrl: 'https://ais-dev-3ye47l3lksx62k4motkf2a-59051396028.asia-east1.run.app/api/webhooks/mercadopago',
    paymentLink: 'https://mpago.la/dojapao-checkout'
  },
  PayPal: {
    id: 'PayPal',
    name: 'PayPal Express',
    badge: 'Global / USD & JPY',
    badgeColor: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    enabled: true,
    isDefault: false,
    useTestMode: true,
    paypalEmail: 'pagamentos@dojapao.co.jp',
    paypalClientId: 'AXdf8913hjk2891d_D3912jksa98dsa91-dojapao-live'
  },
  Stripe: {
    id: 'Stripe',
    name: 'Stripe Checkout',
    badge: 'Apple Pay & Google Pay',
    badgeColor: 'bg-purple-100 text-purple-800 border-purple-300',
    enabled: true,
    isDefault: false,
    useTestMode: true,
    stripePublishableKey: 'pk_live_51M3912KDJSA8912KMDSA9812M',
    stripeSecretKey: ''
  },
  InfinityPay: {
    id: 'InfinityPay',
    name: 'InfinitePay Smart POS',
    badge: 'Parcelado Sem Juros',
    badgeColor: 'bg-amber-100 text-amber-800 border-amber-300',
    enabled: true,
    isDefault: false,
    infinitePayApiKey: '',
    paymentLink: 'https://pay.infinitepay.io/egamicorp',
    maxInstallments: 12,
    interestFreeInstallments: 3
  }
};

export const LOCAL_STORAGE_PAYMENT_CONFIG_KEY = 'dojapao_payment_gateways_config_v1';

export function getSavedPaymentGatewayConfigs(): Record<PaymentGateway, PaymentGatewayConfig> {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_PAYMENT_CONFIG_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const merged = { ...DEFAULT_CONFIGS, ...parsed };
      if (merged.InfinityPay) {
        if (!merged.InfinityPay.paymentLink || merged.InfinityPay.paymentLink.includes('dojapao')) {
          merged.InfinityPay.paymentLink = 'https://pay.infinitepay.io/egamicorp';
        } else {
          merged.InfinityPay.paymentLink = sanitizePaymentUrl(merged.InfinityPay.paymentLink, 'https://pay.infinitepay.io/egamicorp');
        }
      }
      if (merged.MercadoPago && merged.MercadoPago.notificationUrl) {
        merged.MercadoPago.notificationUrl = sanitizePaymentUrl(merged.MercadoPago.notificationUrl);
      }
      return merged;
    }
  } catch (e) {
    console.warn('Could not parse payment gateway configs:', e);
  }
  return DEFAULT_CONFIGS;
}

interface PaymentMethodsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin?: boolean;
}

export const PaymentMethodsModal: React.FC<PaymentMethodsModalProps> = ({
  isOpen,
  onClose,
  isAdmin = false
}) => {
  const [configs, setConfigs] = useState<Record<PaymentGateway, PaymentGatewayConfig>>(getSavedPaymentGatewayConfigs);
  const [activeTab, setActiveTab] = useState<PaymentGateway>('PIX');
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setConfigs(getSavedPaymentGatewayConfigs());
      setSavedSuccess(false);
      setTestResult(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleToggleEnable = (gatewayId: PaymentGateway) => {
    setConfigs(prev => ({
      ...prev,
      [gatewayId]: {
        ...prev[gatewayId],
        enabled: !prev[gatewayId].enabled
      }
    }));
  };

  const handleSetDefault = (gatewayId: PaymentGateway) => {
    setConfigs(prev => {
      const updated = { ...prev };
      (Object.keys(updated) as PaymentGateway[]).forEach(key => {
        updated[key] = {
          ...updated[key],
          isDefault: key === gatewayId
        };
      });
      return updated;
    });
  };

  const handleConfigChange = (gatewayId: PaymentGateway, field: keyof PaymentGatewayConfig, value: any) => {
    setConfigs(prev => ({
      ...prev,
      [gatewayId]: {
        ...prev[gatewayId],
        [field]: value
      }
    }));
  };

  const handleSaveAll = async () => {
    try {
      localStorage.setItem(LOCAL_STORAGE_PAYMENT_CONFIG_KEY, JSON.stringify(configs));
      window.dispatchEvent(new Event('paymentGatewaysUpdated'));
      await setDoc(doc(db, 'settings', 'payment_gateways'), configs);
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3500);
    } catch (err) {
      console.error('Error saving payment configs:', err);
    }
  };

  const handleRestoreDefaults = async () => {
    if (window.confirm('Deseja restaurar as configurações originais de todas as formas de pagamento?')) {
      setConfigs(DEFAULT_CONFIGS);
      localStorage.removeItem(LOCAL_STORAGE_PAYMENT_CONFIG_KEY);
      window.dispatchEvent(new Event('paymentGatewaysUpdated'));
      try {
        await setDoc(doc(db, 'settings', 'payment_gateways'), DEFAULT_CONFIGS);
      } catch (err) {
        console.warn('Error restoring default payment configs in Firestore:', err);
      }
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3500);
    }
  };

  const handleTestConnection = (gatewayId: PaymentGateway) => {
    setTestingConnection(true);
    setTestResult(null);
    setTimeout(() => {
      setTestingConnection(false);
      setTestResult(`Gateway ${configs[gatewayId].name} verificado com sucesso! Credenciais ativas e operacionais para transações BRL/USD/JPY.`);
      setTimeout(() => setTestResult(null), 5000);
    }, 1200);
  };

  const currentConfig = configs[activeTab];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto p-3 sm:p-6 bg-slate-900/75 backdrop-blur-xs animate-fade-in">
      <div className="min-h-full flex items-start sm:items-center justify-center py-4 sm:py-8">
        <div className="bg-white rounded-3xl max-w-4xl w-full shadow-2xl border border-slate-200 overflow-hidden my-auto flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1D3557] via-slate-900 to-[#1D3557] text-white p-5 sm:p-6 flex justify-between items-center relative overflow-hidden shrink-0">
          <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-[radial-gradient(circle_at_top_right,rgba(230,57,70,0.25),transparent_70%)] pointer-events-none" />
          
          <div className="flex items-center gap-3.5 z-10">
            <div className="p-3 bg-white/10 rounded-2xl border border-white/20 shadow-inner">
              <CreditCard className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg sm:text-xl font-black text-white tracking-tight">
                  Gerenciar Formas de Pagamento
                </h3>
                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-[10px] font-extrabold uppercase rounded-full border border-emerald-400/30">
                  Multigateway
                </span>
              </div>
              <p className="text-xs text-slate-300">
                Configure PIX, Mercado Pago, PayPal, Stripe e InfinitePay para cobranças de fretes e loja.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-300 hover:text-white bg-white/10 hover:bg-white/20 rounded-xl transition-colors cursor-pointer z-10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-6">
          
          {/* Quick Info & Default Payment Bar */}
          <div className="bg-slate-50 border border-slate-200/90 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs text-slate-700">
              <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>
                <strong>Modo de Pagamentos:</strong> Os clientes selecionam o gateway preferido durante o checkout de frete internacional ou compras na loja.
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-600">Gateway Padrão:</span>
              <select
                value={(Object.keys(configs) as PaymentGateway[]).find(k => configs[k].isDefault) || 'PIX'}
                onChange={(e) => handleSetDefault(e.target.value as PaymentGateway)}
                className="bg-white border border-slate-300 rounded-xl px-3 py-1 text-xs font-bold text-slate-800 shadow-xs focus:outline-none focus:border-[#1D3557]"
              >
                {(Object.keys(configs) as PaymentGateway[]).map(key => (
                  <option key={key} value={key}>
                    {configs[key].name} {configs[key].enabled ? '' : '(Inativo)'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Gateways Tab Selector Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
            {(Object.keys(configs) as PaymentGateway[]).map((key) => {
              const cfg = configs[key];
              const isSelected = activeTab === key;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  className={`p-3 rounded-2xl border text-left transition-all flex flex-col justify-between cursor-pointer relative ${
                    isSelected
                      ? 'bg-[#1D3557] text-white border-[#1D3557] shadow-md ring-2 ring-[#1D3557]/30'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between w-full mb-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${cfg.enabled ? 'bg-emerald-500 shadow-xs' : 'bg-slate-300'}`} />
                    {cfg.isDefault && (
                      <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded-md ${
                        isSelected ? 'bg-amber-400 text-slate-950' : 'bg-amber-100 text-amber-900 border border-amber-300'
                      }`}>
                        Padrão
                      </span>
                    )}
                  </div>

                  <div>
                    <h4 className={`text-xs font-black tracking-tight ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                      {cfg.name.split(' ')[0]}
                    </h4>
                    <span className={`text-[10px] font-semibold block mt-0.5 ${
                      isSelected ? 'text-slate-300' : 'text-slate-500'
                    }`}>
                      {cfg.badge}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Active Gateway Detailed Configuration Panel */}
          <div className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-xs space-y-5">
            
            {/* Panel Top Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className={`px-3 py-1 rounded-xl text-xs font-black border ${currentConfig.badgeColor}`}>
                  {currentConfig.badge}
                </div>
                <div>
                  <h4 className="text-base font-black text-slate-900 flex items-center gap-2">
                    {currentConfig.name}
                    {currentConfig.isDefault && (
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md">
                        Gateway Preferencial Padrão
                      </span>
                    )}
                  </h4>
                  <p className="text-xs text-slate-500">
                    Ajuste parâmetros, chaves públicas/privadas e preferências de exibição do gateway no checkout.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={currentConfig.enabled}
                    onChange={() => handleToggleEnable(activeTab)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                  <span className="ml-2 text-xs font-bold text-slate-700">
                    {currentConfig.enabled ? 'Ativo na Loja/Fretes' : 'Desativado'}
                  </span>
                </label>
              </div>
            </div>

            {/* TAB SPECIFIC FORM FIELDS */}

            {/* 1. PIX CONFIG */}
            {activeTab === 'PIX' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Chave PIX Oficial</label>
                    <input
                      type="text"
                      value={currentConfig.pixKey || ''}
                      onChange={(e) => handleConfigChange('PIX', 'pixKey', e.target.value)}
                      placeholder="e.g. financeiro@dojapao.co.jp ou CNPJ"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono text-slate-800 focus:bg-white focus:outline-none focus:border-[#1D3557]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Tipo da Chave</label>
                    <select
                      value={currentConfig.pixKeyType || 'email'}
                      onChange={(e) => handleConfigChange('PIX', 'pixKeyType', e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-bold text-slate-800 focus:bg-white focus:outline-none focus:border-[#1D3557]"
                    >
                      <option value="email">E-mail</option>
                      <option value="cnpj">CNPJ / CPF</option>
                      <option value="phone">Telefone</option>
                      <option value="random">Chave Aleatória (EVP)</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Nome do Beneficiário / Titular</label>
                    <input
                      type="text"
                      value={currentConfig.beneficiaryName || ''}
                      onChange={(e) => handleConfigChange('PIX', 'beneficiaryName', e.target.value)}
                      placeholder="Do Japão Logística & Intermediação LTDA"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:border-[#1D3557]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Banco / Instituição Depositária</label>
                    <input
                      type="text"
                      value={currentConfig.bankName || ''}
                      onChange={(e) => handleConfigChange('PIX', 'bankName', e.target.value)}
                      placeholder="Banco Rendimento / Itaú"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:border-[#1D3557]"
                    />
                  </div>
                </div>
                
                
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center justify-between">
                      <span>Código Copia e Cola Fixo</span>
                      <span className="text-[10px] text-slate-400 font-normal">Opcional</span>
                    </label>
                    <input
                      type="text"
                      value={currentConfig.pixCopiaECola || ''}
                      onChange={(e) => handleConfigChange('PIX', 'pixCopiaECola', e.target.value)}
                      placeholder="Cole aqui o payload EMV do seu banco"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:border-emerald-500 font-mono"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      Se preenchido, os clientes copiarão este código exato em vez de um código dinâmico gerado pelo sistema.
                    </p>
                  </div>


                {/* QR Code PIX Image Upload */}
                <div className="p-4 bg-slate-50 border border-slate-200/90 rounded-2xl space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <label className="block text-xs font-bold text-slate-800 flex items-center gap-1.5">
                        <Image className="w-4 h-4 text-emerald-600" />
                        Imagem do QR Code PIX (Upload Admin)
                      </label>
                      <p className="text-[11px] text-slate-500">
                        Envie a imagem do QR Code do seu banco. Ela será exibida automaticamente aos clientes no checkout via PIX.
                      </p>
                    </div>

                    {currentConfig.pixQrCodeImage && (
                      <button
                        type="button"
                        onClick={() => handleConfigChange('PIX', 'pixQrCodeImage', undefined)}
                        className="text-xs font-bold text-rose-600 hover:text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 px-3 py-1.5 rounded-xl transition-colors flex items-center gap-1 cursor-pointer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Remover QR Code</span>
                      </button>
                    )}
                  </div>

                  {currentConfig.pixQrCodeImage ? (
                    <div className="flex flex-col sm:flex-row items-center gap-4 p-3 bg-white border border-slate-200 rounded-xl">
                      <div className="w-28 h-28 shrink-0 bg-slate-50 rounded-xl border border-slate-200 p-1 flex items-center justify-center">
                        <img
                          src={currentConfig.pixQrCodeImage}
                          alt="QR Code PIX Personalizado"
                          className="w-full h-full object-contain rounded-lg"
                        />
                      </div>
                      <div className="text-xs text-slate-600 space-y-1.5 text-center sm:text-left">
                        <span className="font-extrabold text-emerald-700 flex items-center justify-center sm:justify-start gap-1">
                          <CheckCircle2 className="w-4 h-4" /> QR Code PIX Ativo
                        </span>
                        <p className="text-[11px] text-slate-500 max-w-sm">
                          Esta imagem será exibida nos modais de pagamento de frete, compras na loja, leilões e rifas quando o cliente selecionar PIX.
                        </p>
                        <label className="inline-flex items-center gap-1 mt-1 text-xs font-bold text-[#1D3557] hover:text-sky-700 underline cursor-pointer">
                          <Upload className="w-3.5 h-3.5" />
                          <span>Substituir Imagem</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                try {
                                  const webpDataUrl = await convertToWebPDataUrl(file, { maxWidth: 1920, quality: 0.85 });
                                  handleConfigChange('PIX', 'pixQrCodeImage', webpDataUrl);
                                } catch (err) {
                                  console.error('Erro ao converter QR Code para WebP:', err);
                                  const reader = new FileReader();
                                  reader.onload = (event) => {
                                    if (event.target?.result) {
                                      handleConfigChange('PIX', 'pixQrCodeImage', event.target.result as string);
                                    }
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-300 hover:border-emerald-500 bg-white hover:bg-emerald-50/40 rounded-xl cursor-pointer transition-all group">
                      <Upload className="w-8 h-8 text-slate-400 group-hover:text-emerald-600 mb-1 transition-colors" />
                      <span className="text-xs font-bold text-slate-700 group-hover:text-emerald-800">
                        Clique aqui para carregar a imagem do QR Code PIX
                      </span>
                      <span className="text-[10px] text-slate-400 mt-0.5">
                        Convertido automaticamente para WebP (85% qualidade)
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            try {
                              const webpDataUrl = await convertToWebPDataUrl(file, { maxWidth: 1920, quality: 0.85 });
                              handleConfigChange('PIX', 'pixQrCodeImage', webpDataUrl);
                            } catch (err) {
                              console.error('Erro ao converter QR Code para WebP:', err);
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                if (event.target?.result) {
                                  handleConfigChange('PIX', 'pixQrCodeImage', event.target.result as string);
                                }
                              };
                              reader.readAsDataURL(file);
                            }
                          }
                        }}
                      />
                    </label>
                  )}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <div className="flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-emerald-700 shrink-0" />
                    <div>
                      <span className="text-xs font-bold text-emerald-900 block">Exigir Envio de Comprovante PIX</span>
                      <span className="text-[11px] text-emerald-700">O cliente anexa a imagem do comprovante para a equipe conferir em Saitama.</span>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={currentConfig.requireReceipt ?? true}
                    onChange={(e) => handleConfigChange('PIX', 'requireReceipt', e.target.checked)}
                    className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                </div>
              </div>
            )}

            {/* 2. MERCADO PAGO CONFIG */}
            {activeTab === 'MercadoPago' && (
              <div className="space-y-4">
                {/* Test Mode Switch */}
                <div className="flex items-center justify-between p-3.5 bg-sky-50 rounded-2xl border border-sky-200">
                  <div>
                    <span className="text-xs font-bold text-sky-950 block">Modo do Mercado Pago (USE_TEST_MODE)</span>
                    <span className="text-[11px] text-sky-700">
                      {currentConfig.useTestMode !== false 
                        ? '🧪 Modo TESTE (Sandbox) ativo - Credenciais de teste em uso.' 
                        : '🚀 Modo PRODUÇÃO ativo - Transações reais habilitadas.'}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={currentConfig.useTestMode !== false} 
                      onChange={(e) => {
                        const isTest = e.target.checked;
                        setConfigs(prev => ({
                          ...prev,
                          MercadoPago: {
                            ...prev.MercadoPago,
                            useTestMode: isTest,
                            accessToken: isTest 
                              ? (prev.MercadoPago.testAccessToken || prev.MercadoPago.accessToken) 
                              : (prev.MercadoPago.productionAccessToken || prev.MercadoPago.accessToken),
                            publicKey: isTest 
                              ? (prev.MercadoPago.testPublicKey || prev.MercadoPago.publicKey) 
                              : (prev.MercadoPago.productionPublicKey || prev.MercadoPago.publicKey),
                            notificationUrl: isTest 
                              ? (prev.MercadoPago.testNotificationUrl || prev.MercadoPago.notificationUrl) 
                              : (prev.MercadoPago.productionNotificationUrl || prev.MercadoPago.notificationUrl)
                          }
                        }));
                      }} 
                      className="sr-only peer" 
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-600"></div>
                  </label>
                </div>

                {/* TEST CREDENTIALS */}
                <div className="p-4 bg-amber-50/70 border border-amber-200/80 rounded-2xl space-y-3">
                  <span className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                    <Key className="w-3.5 h-3.5 text-amber-600" />
                    <span>Configuração de TESTE (Sandbox)</span>
                  </span>
                  
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Access Token (TESTE)</label>
                    <input
                      type="password"
                      value={currentConfig.testAccessToken || currentConfig.accessToken || ''}
                      onChange={(e) => {
                        handleConfigChange('MercadoPago', 'testAccessToken', e.target.value);
                        if (currentConfig.useTestMode !== false) {
                          handleConfigChange('MercadoPago', 'accessToken', e.target.value);
                        }
                      }}
                      placeholder="APP_USR-... (Chave de Teste)"
                      className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Public Key (TESTE)</label>
                    <input
                      type="text"
                      value={currentConfig.testPublicKey || currentConfig.publicKey || ''}
                      onChange={(e) => {
                        handleConfigChange('MercadoPago', 'testPublicKey', e.target.value);
                        if (currentConfig.useTestMode !== false) {
                          handleConfigChange('MercadoPago', 'publicKey', e.target.value);
                        }
                      }}
                      placeholder="APP_USR-... (Chave Pública de Teste)"
                      className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Notification URL (Webhook Teste)</label>
                    <input
                      type="text"
                      value={currentConfig.testNotificationUrl || currentConfig.notificationUrl || ''}
                      onChange={(e) => {
                        handleConfigChange('MercadoPago', 'testNotificationUrl', e.target.value);
                        if (currentConfig.useTestMode !== false) {
                          handleConfigChange('MercadoPago', 'notificationUrl', e.target.value);
                        }
                      }}
                      placeholder="https://seu-site.com/api/webhooks/mercadopago"
                      className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:border-amber-500"
                    />
                  </div>
                </div>

                {/* PRODUCTION CREDENTIALS */}
                <div className="p-4 bg-emerald-50/70 border border-emerald-200/80 rounded-2xl space-y-3">
                  <span className="text-xs font-bold text-emerald-900 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Configuração de PRODUÇÃO</span>
                  </span>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Access Token (PRODUÇÃO)</label>
                    <input
                      type="password"
                      value={currentConfig.productionAccessToken || ''}
                      onChange={(e) => {
                        handleConfigChange('MercadoPago', 'productionAccessToken', e.target.value);
                        if (currentConfig.useTestMode === false) {
                          handleConfigChange('MercadoPago', 'accessToken', e.target.value);
                        }
                      }}
                      placeholder="APP_USR-... (Chave de Produção Reais)"
                      className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Public Key (PRODUÇÃO)</label>
                    <input
                      type="text"
                      value={currentConfig.productionPublicKey || ''}
                      onChange={(e) => {
                        handleConfigChange('MercadoPago', 'productionPublicKey', e.target.value);
                        if (currentConfig.useTestMode === false) {
                          handleConfigChange('MercadoPago', 'publicKey', e.target.value);
                        }
                      }}
                      placeholder="APP_USR-... (Chave Pública de Produção)"
                      className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Notification URL (Webhook Produção)</label>
                    <input
                      type="text"
                      value={currentConfig.productionNotificationUrl || ''}
                      onChange={(e) => {
                        handleConfigChange('MercadoPago', 'productionNotificationUrl', e.target.value);
                        if (currentConfig.useTestMode === false) {
                          handleConfigChange('MercadoPago', 'notificationUrl', e.target.value);
                        }
                      }}
                      placeholder="https://dojapao.co.jp/api/webhooks/mercadopago"
                      className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Link Base Reserva de Checkout / QR Code</label>
                  <input
                    type="text"
                    value={currentConfig.paymentLink || ''}
                    onChange={(e) => handleConfigChange('MercadoPago', 'paymentLink', e.target.value)}
                    placeholder="https://mpago.la/dojapao-checkout"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:border-[#1D3557]"
                  />
                </div>
              </div>
            )}

            {/* 3. PAYPAL CONFIG */}
            {activeTab === 'PayPal' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">E-mail da Conta PayPal</label>
                    <input
                      type="email"
                      value={currentConfig.paypalEmail || ''}
                      onChange={(e) => handleConfigChange('PayPal', 'paypalEmail', e.target.value)}
                      placeholder="pagamentos@dojapao.co.jp"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:border-[#1D3557]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">PayPal Client ID</label>
                    <input
                      type="text"
                      value={currentConfig.paypalClientId || ''}
                      onChange={(e) => handleConfigChange('PayPal', 'paypalClientId', e.target.value)}
                      placeholder="Client ID da API PayPal Developer"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono text-slate-800 focus:bg-white focus:outline-none focus:border-[#1D3557]"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2.5 py-1">
                  <input
                    type="checkbox"
                    id="paypal_use_test_mode"
                    checked={currentConfig.useTestMode !== false}
                    onChange={(e) => handleConfigChange('PayPal', 'useTestMode', e.target.checked)}
                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer"
                  />
                  <label htmlFor="paypal_use_test_mode" className="text-xs font-black text-slate-700 cursor-pointer select-none">
                    Habilitar Modo Sand (Teste) / Sandbox do PayPal
                  </label>
                </div>

                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-xl text-xs text-indigo-900 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-indigo-600 shrink-0" />
                  <span>Suporta recebimento internacional direto em USD (Dólares), JPY (Ienes) e conversão automática para cartões emitidos no Brasil.</span>
                </div>
              </div>
            )}

            {/* 4. STRIPE CONFIG */}
            {activeTab === 'Stripe' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Stripe Publishable Key (`pk_live_...` / `pk_test_...`)</label>
                  <input
                    type="text"
                    value={currentConfig.stripePublishableKey || ''}
                    onChange={(e) => handleConfigChange('Stripe', 'stripePublishableKey', e.target.value)}
                    placeholder="pk_live_..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono text-slate-800 focus:bg-white focus:outline-none focus:border-[#1D3557]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Stripe Secret Key (`sk_live_...` / `sk_test_...`)</label>
                  <input
                    type="password"
                    value={currentConfig.stripeSecretKey || ''}
                    onChange={(e) => handleConfigChange('Stripe', 'stripeSecretKey', e.target.value)}
                    placeholder="sk_live_..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono text-slate-800 focus:bg-white focus:outline-none focus:border-[#1D3557]"
                  />
                </div>

                <div className="flex items-center gap-2.5 py-1">
                  <input
                    type="checkbox"
                    id="stripe_use_test_mode"
                    checked={currentConfig.useTestMode !== false}
                    onChange={(e) => handleConfigChange('Stripe', 'useTestMode', e.target.checked)}
                    className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 border-slate-300 cursor-pointer"
                  />
                  <label htmlFor="stripe_use_test_mode" className="text-xs font-black text-slate-700 cursor-pointer select-none">
                    Habilitar Modo Sand (Teste) do Stripe
                  </label>
                </div>

                <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl text-xs text-purple-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600 shrink-0" />
                  <span>Compatível com Apple Pay, Google Pay e todas as bandeiras internacionais de cartão de crédito.</span>
                </div>
              </div>
            )}

            {/* 5. INFINITEPAY CONFIG */}
            {activeTab === 'InfinityPay' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Link de Pagamento InfinitePay</label>
                    <input
                      type="text"
                      value={currentConfig.paymentLink || ''}
                      onChange={(e) => handleConfigChange('InfinityPay', 'paymentLink', e.target.value)}
                      placeholder="https://pay.infinitepay.io/egamicorp"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:border-[#1D3557]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Máximo de Parcelas Permitidas</label>
                    <select
                      value={currentConfig.maxInstallments || 12}
                      onChange={(e) => handleConfigChange('InfinityPay', 'maxInstallments', Number(e.target.value))}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-bold text-slate-800 focus:bg-white focus:outline-none focus:border-[#1D3557]"
                    >
                      <option value={1}>1x (À vista)</option>
                      <option value={3}>Até 3x</option>
                      <option value={6}>Até 6x</option>
                      <option value={10}>Até 10x</option>
                      <option value={12}>Até 12x</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">InfinitePay API Key / Token</label>
                  <input
                    type="password"
                    value={currentConfig.infinitePayApiKey || ''}
                    onChange={(e) => handleConfigChange('InfinityPay', 'infinitePayApiKey', e.target.value)}
                    placeholder="inf_live_..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono text-slate-800 focus:bg-white focus:outline-none focus:border-[#1D3557]"
                  />
                </div>
              </div>
            )}

            {/* Test Connection Button & Result */}
            <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                disabled={testingConnection}
                onClick={() => handleTestConnection(activeTab)}
                className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-60"
              >
                {testingConnection ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-600" />
                ) : (
                  <Zap className="w-3.5 h-3.5 text-amber-500" />
                )}
                <span>Testar Conexão com {currentConfig.name}</span>
              </button>

              {testResult && (
                <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-xl font-medium flex items-center gap-1.5 animate-fade-in">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>{testResult}</span>
                </div>
              )}
            </div>

          </div>

          {/* Feedback message */}
          {savedSuccess && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 p-3.5 rounded-2xl text-xs text-emerald-900 font-bold flex items-center gap-2 animate-fade-in">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <span>Configurações das formas de pagamento atualizadas e salvas com sucesso!</span>
            </div>
          )}

        </div>

        {/* Footer Actions */}
        <div className="p-4 sm:p-5 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <button
            type="button"
            onClick={handleRestoreDefaults}
            className="px-3.5 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 rounded-xl text-xs font-bold transition-colors cursor-pointer"
          >
            Restaurar Padrões
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl text-xs font-bold transition-colors cursor-pointer"
            >
              Fechar
            </button>
            <button
              type="button"
              onClick={handleSaveAll}
              className="px-5 py-2.5 bg-[#1D3557] hover:bg-[#152741] text-white rounded-xl text-xs font-black transition-all shadow-md flex items-center gap-2 cursor-pointer"
            >
              <Save className="w-4 h-4" />
              <span>Salvar Alterações</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
  );
};

export default PaymentMethodsModal;
